import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

/**
 * Strips File/Blob values from an object tree so sessionStorage serialization works.
 * After a page refresh, file fields will appear empty and must be re-uploaded.
 *
 * @param {*} obj
 * @returns {*}
 */
const stripFiles = (obj) => {
  if (obj === null || obj === undefined) return obj;
  if (obj instanceof File || obj instanceof Blob) return undefined;
  if (Array.isArray(obj)) return obj.map(stripFiles);
  if (typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj)
        .map(([k, v]) => [k, stripFiles(v)])
        .filter(([, v]) => v !== undefined)
    );
  }
  return obj;
};

/**
 * Factory that creates a Zustand store for a multi-step onboarding flow.
 *
 * @param {string} storageKey   — sessionStorage key for persistence
 * @param {number} totalSteps   — default number of steps in the flow
 * @returns {Function}
 */
const createOnboardingStore = (storageKey, totalSteps) =>
  create(
    persist(
      (set) => ({
        currentStep: 1,
        completedStepIds: [],
        stepData: {},
        stepReviewStatuses: {},
        showSuccess: false,
        pageLoading: true,

        /**
         * Mark a step complete, store its data, and advance.
         * If re-completing an earlier step, invalidate all downstream steps.
         *
         * @param {number} stepNumber
         * @param {object} data
         * @param {number} [activeTotalSteps] — override totalSteps (e.g. DNFI dynamic steps)
         * @returns {void}
         */
        completeStep: (stepNumber, data, activeTotalSteps) =>
          set((state) => {
            const total = activeTotalSteps ?? totalSteps;
            const isRecompletion = state.completedStepIds.includes(stepNumber);

            // Invalidate downstream steps on re-completion
            let newCompletedStepIds = state.completedStepIds;
            let newStepData = { ...state.stepData, [stepNumber]: data };

            if (isRecompletion) {
              newCompletedStepIds = state.completedStepIds.filter((id) => id <= stepNumber);
            } else {
              newCompletedStepIds = [...state.completedStepIds, stepNumber];
            }

            const isLastStep = stepNumber >= total;

            return {
              completedStepIds: newCompletedStepIds,
              stepData: newStepData,
              currentStep: isLastStep ? state.currentStep : stepNumber + 1,
              showSuccess: isLastStep ? true : state.showSuccess
            };
          }),

        setStepReviewStatuses: (statuses) => set({ stepReviewStatuses: statuses }),

        /**
         * Navigate to a step (only if completed or current).
         * In re-submission mode (stepReviewStatuses populated), all steps are navigable.
         *
         * @param {number} stepId
         * @returns {void}
         */
        navigateToStep: (stepId) =>
          set((state) => {
            const isResubmission = Object.keys(state.stepReviewStatuses).length > 0;
            if (
              isResubmission ||
              state.completedStepIds.includes(stepId) ||
              stepId === state.currentStep
            ) {
              return { currentStep: stepId };
            }
            return {};
          }),

        /**
         * Merge partial data into a step's data without advancing the flow.
         * Used for auto-saving drafts when the user modifies form state.
         *
         * @param {number} stepNumber
         * @param {object} partialData
         * @returns {void}
         */
        updateStepData: (stepNumber, partialData) =>
          set((state) => ({
            stepData: {
              ...state.stepData,
              [stepNumber]: { ...state.stepData[stepNumber], ...partialData }
            }
          })),

        setPageLoading: (loading) => set({ pageLoading: loading }),

        resetFlow: () =>
          set({
            currentStep: 1,
            completedStepIds: [],
            stepData: {},
            stepReviewStatuses: {},
            showSuccess: false,
            pageLoading: true
          })
      }),
      {
        name: storageKey,
        storage: createJSONStorage(() => sessionStorage),
        partialize: (state) => ({
          currentStep: state.currentStep,
          completedStepIds: state.completedStepIds,
          stepData: stripFiles(state.stepData),
          showSuccess: state.showSuccess
          // pageLoading excluded — always starts true
          // stepReviewStatuses excluded — re-derived from API on init
        })
      }
    )
  );

export { createOnboardingStore };
